import React, { useEffect, useState } from 'react';
import { Box, EmptyContent, Flex } from '@/components/ui';
import { PreviewRendererProps } from '../types';
import { IconAlertCircle, IconPlayerPlay } from '@tabler/icons-react';
import { api } from '@/services/http';

const VideoPreviewRenderer: React.FC<PreviewRendererProps> = ({
  apiEndpoint,
  mimeType,
}) => {
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Check if the browser supports this video type
  const videoElement = document.createElement('video');
  const canPlay = mimeType ? videoElement.canPlayType(mimeType) : '';

  // Load video with authentication
  useEffect(() => {
    let isCancelled = false;

    const loadVideo = async () => {
      if (!apiEndpoint) {
        setVideoError('No API endpoint provided');
        setVideoLoading(false);
        return;
      }

      if (!canPlay) {
        setVideoError('Video format not supported');
        setVideoLoading(false);
        return;
      }

      try {
        const response = await api.get(apiEndpoint, {
          responseType: 'blob',
          headers: {
            Accept: 'video/*',
          },
        });

        if (isCancelled) return;

        const blobUrl = URL.createObjectURL(response.data);
        setVideoBlobUrl(blobUrl);
        setVideoLoading(false);
      } catch {
        if (!isCancelled) {
          setVideoError('Failed to load video');
          setVideoLoading(false);
        }
      }
    };

    loadVideo();

    return () => {
      isCancelled = true;
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
      }
    };
  }, [apiEndpoint, videoBlobUrl, canPlay]);

  if (!canPlay) {
    return (
      <EmptyContent
        size="medium"
        state="warning"
        icon={<IconAlertCircle />}
        title="Video Format Not Supported"
        description="Your browser cannot play this video format. Please download the file to view it with an external player."
      />
    );
  }

  if (videoError) {
    return (
      <EmptyContent
        size="medium"
        state="warning"
        icon={<IconAlertCircle />}
        title="Video Preview Error"
        description={videoError}
      />
    );
  }

  return (
    <Flex direction="column" align="center" gap="3">
      <Box
        style={{
          width: '100%',
          maxWidth: '800px',
          maxHeight: '70vh',
        }}
      >
        {videoLoading ? (
          <EmptyContent
            size="medium"
            icon={<IconPlayerPlay />}
            title="Loading Video"
            description="Please wait while the video loads..."
          />
        ) : (
          <video
            src={videoBlobUrl || undefined}
            controls
            preload="metadata"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <EmptyContent
              size="medium"
              icon={<IconPlayerPlay />}
              title="Video Not Supported"
              description="Your browser does not support the video element or this video format."
            />
          </video>
        )}
      </Box>
    </Flex>
  );
};

export default VideoPreviewRenderer;
